import {Component, DestroyRef, EventEmitter, inject, Input, signal, SimpleChanges, WritableSignal} from '@angular/core';
import {NgClass, NgIf, NgStyle} from '@angular/common';
import {ICellRendererAngularComp} from 'ag-grid-angular';
import {addPercent, round} from '../../../../../utils/numbers';
import {CCDEvent, CCDEventType} from '../../types/types';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-value-cell',
  standalone: true,
  imports: [
    NgIf,
    NgStyle,
    NgClass
  ],
  templateUrl: './value-cell.component.html',
  styleUrl: './value-cell.component.scss'
})
export class ValueCellComponent implements ICellRendererAngularComp {
  @Input({required: true}) color!: string;
  @Input() events: EventEmitter<CCDEvent> | undefined;
  @Input() value: number = 0;
  @Input() brandKey: string | undefined;
  @Input() isColumnsHover: WritableSignal<boolean> = signal(false);

  protected barWidth = "0%";
  protected isHovered = false;
  protected focus: WritableSignal<boolean> = signal<boolean>(false);
  protected blur: WritableSignal<boolean> = signal<boolean>(false);
  protected readonly round = round;
  protected readonly addPercent = addPercent;

  private destroyRef = inject(DestroyRef);

  agInit(params: any): void {
    this.setParams(params);
    this.setBarWidth();
  }

  refresh(params: any): boolean {
    this.setParams(params);
    this.setBarWidth();
    return true;
  }

  private setParams(params: any): void {
    this.isColumnsHover = params.isColumnsHover;
    this.value = Number(params.value ?? 0);
    this.brandKey = params.brandKey || '';
    this.color = params?.color ?? params?.cellRendererParams?.color ?? this.color;
    this.events = params.events;
    this.events?.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event: CCDEvent) => {
        switch (event.type) {
          case CCDEventType.HOVER_IN:
            if (event.colId === this.brandKey) {
              this.isHovered = true;
            }
            this.setFocusAndBlur();
            break;
          case CCDEventType.HOVER_OUT:
            if (event.colId === this.brandKey) {
              this.isHovered = false;
            }
            this.setFocusAndBlur();
            break;
        }
      })
  }

  private setBarWidth(): void {
    const width = Math.min(Math.max(this.value, 0), 100);
    this.barWidth = `${width}%`;
  }

  private setFocusAndBlur(): void {
    this.focus.set(this.isColumnsHover() && this.isHovered);
    this.blur.set(this.isColumnsHover() && !this.isHovered);
  }
}
